import { AppBar, Box, Button, TextField, Toolbar, Typography, Checkbox, FormGroup, FormControlLabel, Paper, Divider, Stack } from '@mui/material'
import React, { useEffect, useState } from 'react'
import LoadingBackdrop from '../../Common/LoadingBackdrop'
import { Cancel, Save } from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOneRole, saveRole } from '../../../actions/rolesActions';
import { fetchAllPermissions } from '../../../actions/permissionsActions';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

export default function RoleFormPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [role, setRole] = useState({
    role_id: 0,
    role_name: '',
    role_description: '',
    permissions: []
  });

  const loading = useSelector(state => state.roles.loading);
  const roleStore = useSelector(state => state.roles.role);
  const permissions = useSelector(state => state.permissions.permissions);

  // Fetch permissions only once when component mounts
  useEffect(() => {
    dispatch(fetchAllPermissions());
  }, []);

  // Fetch role data when component mounts or when id changes
  useEffect(() => {
    if (id) {
      dispatch(fetchOneRole(id));
    }
  }, [id]);

  // Update local state when roleStore changes
  useEffect(() => {
    if (!id) {
      // Reset state when in create mode
      setRole({
        role_id: 0,
        role_name: '',
        role_description: '',
        permissions: []
      });
    } else if (roleStore) {
      // Extract role data and permissions from the API response
      const roleData = roleStore.role || roleStore;
      const rolePermissions = roleStore.permissions || [];

      // Map permissions to just their IDs for the role object
      const permissionIds = rolePermissions.map(p => p.permission_id);

      setRole({
        ...roleData,
        permissions: permissionIds
      });
    }
  }, [roleStore, id]);

  const handleSave = e => {
    e.preventDefault();

    const roleToSave = {
      ...role,
    };

    dispatch(saveRole(roleToSave, () => navigate('/roles')));
  }

  const handleChange = e => {
    setRole({
      ...role,
      [e.target.name]: e.target.value
    })
  }

  const handlePermissionChange = (permissionId) => {
    const updatedPermissions = role.permissions.includes(permissionId)
      ? role.permissions.filter(id => id !== permissionId)
      : [...role.permissions, permissionId];

    setRole({
      ...role,
      permissions: updatedPermissions
    });
  }

  const handleSelectAll = (category) => {
    const categoryPermissions = permissions
      .filter(p => p.permission_category === category)
      .map(p => p.permission_id);

    const hasAllSelected = categoryPermissions.every(id =>
      role.permissions.includes(id)
    );

    const updatedPermissions = hasAllSelected
      ? role.permissions.filter(id => !categoryPermissions.includes(id))
      : [...new Set([...role.permissions, ...categoryPermissions])];

    setRole({
      ...role,
      permissions: updatedPermissions
    });
  }

  const groupPermissionsByCategory = () => {
    const grouped = {};
    permissions.forEach(permission => {
      if (!grouped[permission.permission_category]) {
        grouped[permission.permission_category] = [];
      }
      grouped[permission.permission_category].push(permission);
    });
    return grouped;
  }

  // Convert snake_case to Title Case for display
  const formatDisplayName = (name) => {
    // First convert snake_case to spaces
    const withSpaces = name.replace(/_/g, ' ');
    // Then capitalize each word
    return withSpaces.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Format category name for display
  const formatCategoryName = (category) => {
    return formatDisplayName(category);
  }

  const groupedPermissions = groupPermissionsByCategory();

  return (
    <MainContentContainer sx={{ py: 2 }} component="form" onSubmit={handleSave}>
      <LoadingBackdrop open={loading} />
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {role.role_id > 0 ? 'Edit' : 'Create'} Role
          </Typography>
          <Button
            color="success"
            type="submit"
            startIcon={<Save />}
            variant="contained"
            sx={{ marginRight: 2 }}
            disabled={loading}
          >
            Save
          </Button>
          <Button
            color="error"
            component={Link}
            to={`/roles`}
            startIcon={<Cancel />}
            variant="contained"
            disabled={loading}
          >
            Cancel
          </Button>
        </Toolbar>
      </AppBar>
      <Box gap={2} sx={{ mt: 2 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <TextField
              fullWidth
              label="Role Name"
              variant="outlined"
              name="role_name"
              value={role.role_name}
              onChange={handleChange}
              autoFocus
              required
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              name="role_description"
              value={role.role_description}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </Stack>
        </Paper>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Permissions
        </Typography>

        {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
          <Paper key={category} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                {formatCategoryName(category)}
              </Typography>
              <Button
                size="small"
                onClick={() => handleSelectAll(category)}
              >
                Select All
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <FormGroup>
              {categoryPermissions.map(permission => (
                <FormControlLabel
                  key={permission.permission_id}
                  control={
                    <Checkbox
                      checked={role.permissions.includes(permission.permission_id)}
                      onChange={() => handlePermissionChange(permission.permission_id)}
                    />
                  }
                  label={formatDisplayName(permission.permission_name)}
                />
              ))}
            </FormGroup>
          </Paper>
        ))}
      </Box>
    </MainContentContainer>
  )
} 